/** Forecast scenario defaults and localStorage key. */

import {
  DEFAULT_CHANNELS,
  PAID_BUDGET_MODE_CUSTOM,
  PAID_BUDGET_MODE_LINK,
} from './channels';
import { cloneDefaultMarketingPhases, normalizeMarketingPhases } from './marketingPhases';
import {
  createAllScenarioPacks,
  FFC_DEFAULT_PLAID_COGS_PER_LINKED_LOGO,
  FFC_DEFAULT_STRIPE_COGS_PER_PAID_LOGO,
} from './scenarios';
import { normalizeFunnel, normalizePlanMixGrowth } from './funnel';
import { normalizeGrowth, normalizeGrowthRegimeMix } from './growthOptions';
import { normalizeRetention } from './cohorts';

export const SCENARIO_STORAGE_KEY = 'ffc.admin.forecast.scenario.v7';
export const LEGACY_SCENARIO_STORAGE_KEY = 'ffc.admin.forecast.scenario.v6';
export const LEGACY_SCENARIO_STORAGE_KEY_V5 = 'ffc.admin.forecast.scenario.v5';
export const LEGACY_SCENARIO_STORAGE_KEY_V4 = 'ffc.admin.forecast.scenario.v4';
export const LEGACY_SCENARIO_STORAGE_KEY_V3 = 'ffc.admin.forecast.scenario.v3';
export const LEGACY_SCENARIO_STORAGE_KEY_V2 = 'ffc.admin.forecast.scenario.v2';
export const LEGACY_SCENARIO_STORAGE_KEY_V1 = 'ffc.admin.forecast.scenario.v1';

export const DEFAULT_MONTE_CARLO = {
  enabled: true,
  // 500×60mo freezes the SPA on every assumption keystroke; 150 is enough for P10/P50/P90.
  runs: 150,
  seed: 20260812,
  conversionVolPct: 20,
  churnVolPct: 15,
  cacVolPct: 25,
  // Sample fall_flat / traction / explode each MC run (not just vol around Traction).
  mixGrowthRegimes: true,
  growthRegimeMix: {
    fall_flat: 0.25,
    traction: 0.5,
    explode: 0.25,
  },
};

function finiteNumber(value, fallback, min = 0, max = Infinity) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(max, Math.max(min, parsed));
}

function normalizeBoolean(value, fallback = false) {
  if (value === true || value === 1 || value === 'true') return true;
  if (value === false || value === 0 || value === 'false') return false;
  return fallback;
}

function normalizeAssumptionNumbers(assumptions, defaults) {
  const source = assumptions || {};
  const legacyVariableCogs = finiteNumber(
    source.variableCogsPerLogo,
    defaults.variableCogsPerLogo
  );
  const stripeCogsPerPaidLogo = finiteNumber(
    source.stripeCogsPerPaidLogo,
    FFC_DEFAULT_STRIPE_COGS_PER_PAID_LOGO
  );
  const plaidCogsPerLinkedLogo = finiteNumber(
    source.plaidCogsPerLinkedLogo,
    Math.max(0, legacyVariableCogs - stripeCogsPerPaidLogo)
  );
  const arpuByTier = {
    ...defaults.arpuByTier,
    ...(source.arpuByTier || {}),
  };
  const planMix = {
    ...defaults.planMix,
    ...(source.planMix || {}),
  };
  return {
    ...defaults,
    ...source,
    openingMrr: finiteNumber(source.openingMrr, defaults.openingMrr),
    openingLogos: finiteNumber(source.openingLogos, defaults.openingLogos),
    startingCash: finiteNumber(source.startingCash, defaults.startingCash),
    fixedOpexMo: finiteNumber(source.fixedOpexMo, defaults.fixedOpexMo),
    variableCogsPerLogo: legacyVariableCogs,
    plaidCogsPerLinkedLogo: Number.isFinite(plaidCogsPerLinkedLogo)
      ? plaidCogsPerLinkedLogo
      : FFC_DEFAULT_PLAID_COGS_PER_LINKED_LOGO,
    stripeCogsPerPaidLogo,
    aiCogsPerLogo: finiteNumber(source.aiCogsPerLogo, defaults.aiCogsPerLogo),
    aiPackRevenuePerLogo: finiteNumber(
      source.aiPackRevenuePerLogo,
      defaults.aiPackRevenuePerLogo
    ),
    horizonMonths: Math.floor(
      finiteNumber(source.horizonMonths, defaults.horizonMonths, 1, 60)
    ),
    expansionPctMo: finiteNumber(source.expansionPctMo, defaults.expansionPctMo, 0, 20),
    contractionPctMo: finiteNumber(
      source.contractionPctMo,
      defaults.contractionPctMo,
      0,
      20
    ),
    arpuByTier: Object.fromEntries(
      Object.entries(arpuByTier).map(([tier, value]) => [
        tier,
        finiteNumber(value, defaults.arpuByTier?.[tier] ?? 0),
      ])
    ),
    planMix: Object.fromEntries(
      Object.entries(planMix).map(([tier, value]) => [
        tier,
        finiteNumber(value, defaults.planMix?.[tier] ?? 0, 0, 1),
      ])
    ),
    planMixGrowth: normalizePlanMixGrowth({
      ...defaults.planMixGrowth,
      ...(source.planMixGrowth || {}),
    }),
  };
}

function normalizeChannels(channels, marketingBudgetMo) {
  const source = channels || {};
  const organic = { ...DEFAULT_CHANNELS.organic, ...(source.organic || {}) };
  const paid = normalizePaidChannel(
    { ...DEFAULT_CHANNELS.paid, ...(source.paid || {}) },
    marketingBudgetMo
  );
  const referral = { ...DEFAULT_CHANNELS.referral, ...(source.referral || {}) };
  return {
    ...DEFAULT_CHANNELS,
    ...source,
    enabled: normalizeBoolean(source.enabled, true),
    organic: {
      ...organic,
      mode: organic.mode === 'absolute' ? 'absolute' : 'growth',
      monthlySignups: finiteNumber(organic.monthlySignups, 0),
      growthPctMo: finiteNumber(organic.growthPctMo, 0, -50, 100),
    },
    paid,
    referral: {
      ...referral,
      enabled: normalizeBoolean(referral.enabled, false),
      kFactor: finiteNumber(referral.kFactor, 0, 0, 2),
      lagMonths: Math.floor(finiteNumber(referral.lagMonths, 1, 0, 6)),
    },
    tamCap:
      source.tamCap == null
        ? null
        : finiteNumber(source.tamCap, 0),
  };
}

function normalizeEvents(events) {
  if (!Array.isArray(events)) return [];
  const allowedTypes = new Set([
    'launch',
    'campaign',
    'pricing',
    'churn',
    'cash_injection',
    'other',
  ]);
  return events.map((event) => {
    const type = allowedTypes.has(event?.type) ? event.type : 'other';
    return {
      ...event,
      type,
      monthIndex: Math.floor(finiteNumber(event?.monthIndex, 0, 0, 60)),
      label: typeof event?.label === 'string' ? event.label : '',
      deltaSignups:
        type === 'churn' || type === 'cash_injection'
          ? 0
          : finiteNumber(event?.deltaSignups, 0, -Infinity),
      mrrShock:
        type === 'churn' || type === 'cash_injection'
          ? 0
          : finiteNumber(event?.mrrShock, 0, -Infinity),
      conversionDeltaPp:
        type === 'churn' || type === 'cash_injection'
          ? 0
          : finiteNumber(event?.conversionDeltaPp, 0, -100, 100),
      churnDeltaPp:
        type === 'churn'
          ? finiteNumber(event?.churnDeltaPp, 0, -100, 100)
          : 0,
      amount:
        type === 'cash_injection'
          ? finiteNumber(event?.amount ?? event?.cashInjection, 0)
          : 0,
      cashInjection: 0,
    };
  });
}

function normalizePaidChannel(paid, marketingBudgetMo) {
  if (!paid) return paid;
  const budgetMode =
    paid.budgetMode === PAID_BUDGET_MODE_CUSTOM
      ? PAID_BUDGET_MODE_CUSTOM
      : PAID_BUDGET_MODE_LINK;
  return {
    ...paid,
    enabled: normalizeBoolean(paid.enabled, true),
    budgetMode,
    budgetMo: finiteNumber(
      budgetMode === PAID_BUDGET_MODE_LINK
        ? marketingBudgetMo ?? paid.budgetMo ?? 0
        : paid.budgetMo ?? 0,
      0
    ),
    cac: finiteNumber(paid.cac, 1, 1),
  };
}

function normalizeAssumptions(assumptions, defaults) {
  const hasMarketingPhasesField = Array.isArray(assumptions?.marketingPhases);
  const hasImportedPhases =
    hasMarketingPhasesField && assumptions.marketingPhases.length > 0;
  const fallbackPhases = cloneDefaultMarketingPhases();
  if (!hasImportedPhases && assumptions?.marketingBudgetMo != null) {
    const importedBudget = Number(assumptions.marketingBudgetMo);
    if (Number.isFinite(importedBudget)) {
      fallbackPhases[0].budgetMo = Math.max(0, importedBudget);
    }
  }
  const marketingPhases = hasMarketingPhasesField
    ? hasImportedPhases
      ? normalizeMarketingPhases(assumptions.marketingPhases.map((p) => ({ ...p })))
      : []
    : normalizeMarketingPhases(fallbackPhases);
  const normalized = normalizeAssumptionNumbers(assumptions, defaults);
  return {
    ...normalized,
    marketingPhases,
    // The first phase is canonical once phased marketing exists. Keeping a
    // second stale scalar here made the linked paid-channel preview disagree
    // with the actual cash/acquisition schedule after legacy migration.
    marketingBudgetMo:
      marketingPhases[0]?.budgetMo ??
      normalized.marketingBudgetMo ??
      defaults.marketingBudgetMo,
  };
}

export function createDefaultScenario() {
  return {
    version: 7,
    asOf: '2026-08-12',
    activeScenario: 'base',
    compareMode: 'active',
    packs: createAllScenarioPacks(),
    monteCarlo: { ...DEFAULT_MONTE_CARLO },
    syncedFromEconomics: null,
    syncedActuals: null,
    defaultsNote:
      'FFC v7: phased marketing (no Meta Ads); AI included quota + overage packs; Traction organic',
  };
}

export function mergeScenarioDefaults(parsed) {
  const defaults = createDefaultScenario();
  if (!parsed || typeof parsed !== 'object') return defaults;

  const packs = { ...defaults.packs };
  for (const key of Object.keys(packs)) {
    if (parsed.packs?.[key]) {
      const mergedAssumptions = normalizeAssumptions(
        parsed.packs[key].assumptions,
        packs[key].assumptions
      );
      const mergedChannels = normalizeChannels(
        {
          ...packs[key].channels,
          ...parsed.packs[key].channels,
          paid: {
            ...packs[key].channels?.paid,
            ...parsed.packs[key].channels?.paid,
          },
          organic: {
            ...packs[key].channels?.organic,
            ...parsed.packs[key].channels?.organic,
          },
          referral: {
            ...packs[key].channels?.referral,
            ...parsed.packs[key].channels?.referral,
          },
        },
        mergedAssumptions.marketingBudgetMo
      );
      packs[key] = {
        ...packs[key],
        ...parsed.packs[key],
        assumptions: mergedAssumptions,
        funnel: normalizeFunnel({ ...packs[key].funnel, ...parsed.packs[key].funnel }),
        cohorts: {
          ...packs[key].cohorts,
          ...parsed.packs[key].cohorts,
          retention: normalizeRetention(
            parsed.packs[key].cohorts?.retention ?? packs[key].cohorts?.retention
          ),
        },
        channels: mergedChannels,
        growth: normalizeGrowth({
          ...packs[key].growth,
          ...parsed.packs[key].growth,
        }),
        events: normalizeEvents(parsed.packs[key].events ?? packs[key].events),
      };
    }
  }

  return {
    ...defaults,
    ...parsed,
    version: 7,
    activeScenario: ['bear', 'base', 'bull'].includes(parsed.activeScenario)
      ? parsed.activeScenario
      : defaults.activeScenario,
    compareMode: parsed.compareMode === 'all' ? 'all' : 'active',
    packs,
    monteCarlo: {
      ...DEFAULT_MONTE_CARLO,
      ...parsed.monteCarlo,
      enabled: normalizeBoolean(parsed.monteCarlo?.enabled, DEFAULT_MONTE_CARLO.enabled),
      mixGrowthRegimes: normalizeBoolean(
        parsed.monteCarlo?.mixGrowthRegimes,
        DEFAULT_MONTE_CARLO.mixGrowthRegimes
      ),
      seed: Number.isFinite(Number(parsed.monteCarlo?.seed))
        ? Math.floor(Number(parsed.monteCarlo.seed))
        : DEFAULT_MONTE_CARLO.seed,
      runs: Math.min(
        5000,
        Math.max(50, Math.floor(Number(parsed.monteCarlo?.runs) || 150))
      ),
      conversionVolPct: Math.min(
        100,
        Math.max(
          0,
          Number.isFinite(Number(parsed.monteCarlo?.conversionVolPct))
            ? Number(parsed.monteCarlo.conversionVolPct)
            : DEFAULT_MONTE_CARLO.conversionVolPct
        )
      ),
      churnVolPct: Math.min(
        100,
        Math.max(
          0,
          Number.isFinite(Number(parsed.monteCarlo?.churnVolPct))
            ? Number(parsed.monteCarlo.churnVolPct)
            : DEFAULT_MONTE_CARLO.churnVolPct
        )
      ),
      cacVolPct: Math.min(
        100,
        Math.max(
          0,
          Number.isFinite(Number(parsed.monteCarlo?.cacVolPct))
            ? Number(parsed.monteCarlo.cacVolPct)
            : DEFAULT_MONTE_CARLO.cacVolPct
        )
      ),
      growthRegimeMix: {
        ...normalizeGrowthRegimeMix(parsed.monteCarlo?.growthRegimeMix),
      },
    },
    syncedActuals: parsed.syncedActuals ?? defaults.syncedActuals,
  };
}
